/**
 * Semantic Search Test Component
 * Development testing component for semantic search functionality
 */

import React, { useState } from "react";
import { useSemanticSearch } from "../../hooks/useSemanticSearch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Search, Loader2, AlertCircle, Clock, ChefHat } from "lucide-react";

export const SemanticSearchTest: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isManualSearch, setIsManualSearch] = useState(false);

  const {
    recipes,
    searchMetadata,
    relatedQueries,
    filterSuggestions,
    isLoading,
    isEmpty,
    hasQuery,
    error,
    updateQuery,
    search,
    performanceMetrics,
    personalizationInsights,
    hasResults,
  } = useSemanticSearch({
    query: searchQuery,
    limit: 10,
    usePersonalization: true,
    enabled: isManualSearch || searchQuery.trim().length > 0,
  });

  const handleSearch = () => {
    updateQuery(searchQuery);
    setIsManualSearch(true);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <div className="mb-8">
        <h1 className="mb-4 flex items-center gap-3 text-3xl font-bold text-gray-900">
          <Search className="h-8 w-8 text-blue-600" />
          Semantic Search Test
        </h1>
        <p className="text-gray-600">
          Test the semantic search functionality with natural language queries
        </p>
      </div>

      {/* Search Input */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <Input
              placeholder="Search for recipes... (e.g., 'healthy pasta with vegetables')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1"
            />
            <Button
              onClick={handleSearch}
              disabled={isLoading || !searchQuery.trim()}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Metadata & Performance */}
      {searchMetadata && performanceMetrics && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-sm">Search Analytics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
              <div>
                <span className="text-gray-600">Search Type:</span>
                <div className="font-semibold">{searchMetadata.searchType}</div>
              </div>
              <div>
                <span className="text-gray-600">Execution Time:</span>
                <div className="font-semibold">
                  {performanceMetrics.executionTime}ms
                </div>
              </div>
              <div>
                <span className="text-gray-600">Results Found:</span>
                <div className="font-semibold">{searchMetadata.totalCount}</div>
              </div>
              <div>
                <span className="text-gray-600">Avg Relevance:</span>
                <div className="font-semibold">
                  {performanceMetrics.relevanceScore.toFixed(2)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Personalization Insights */}
      {personalizationInsights &&
        personalizationInsights.personalizedCount > 0 && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-sm text-green-800">
                Personalization Active
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-green-700">
                <p>
                  {personalizationInsights.personalizedCount} of{" "}
                  {recipes.length} recipes personalized (
                  {personalizationInsights.personalizedPercentage.toFixed(1)}%)
                </p>
                {personalizationInsights.topMatchingFactors.length > 0 && (
                  <div className="mt-2">
                    <span className="font-medium">Top matching factors: </span>
                    {personalizationInsights.topMatchingFactors.map(
                      ({ factor, count }) => (
                        <Badge
                          key={factor}
                          variant="secondary"
                          className="mr-1 text-xs"
                        >
                          {factor} ({count})
                        </Badge>
                      ),
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Search Error</span>
            </div>
            <p className="mt-1 text-red-700">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-gray-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Searching recipes...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {isEmpty && hasQuery && !isLoading && (
        <Card className="mb-6">
          <CardContent className="pt-6 text-center">
            <Search className="mx-auto mb-3 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              No recipes found
            </h3>
            <p className="mb-4 text-gray-600">
              Try adjusting your search terms or using different keywords
            </p>
            {relatedQueries.length > 0 && (
              <div>
                <p className="mb-2 text-sm text-gray-600">
                  Suggested searches:
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {relatedQueries.slice(0, 3).map((suggestion, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchQuery(suggestion);
                        updateQuery(suggestion);
                      }}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recipe Results */}
      {hasResults && !isLoading && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Search Results ({recipes.length})
          </h2>

          <div className="grid gap-4">
            {recipes.map((result, index) => (
              <Card
                key={result.recipe.id || index}
                className="transition-shadow hover:shadow-md"
              >
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    {result.recipe.imageUrl && (
                      <img
                        src={result.recipe.imageUrl}
                        alt={result.recipe.title}
                        className="h-24 w-24 rounded-lg object-cover"
                      />
                    )}

                    <div className="flex-1">
                      <div className="mb-2 flex items-start justify-between">
                        <h3 className="line-clamp-2 font-semibold text-gray-900">
                          {result.recipe.title}
                        </h3>
                        <div className="ml-4 flex items-center gap-2">
                          {result.isPersonalizedMatch && (
                            <Badge className="bg-green-100 text-xs text-green-800">
                              For You
                            </Badge>
                          )}
                          <Badge variant="secondary" className="text-xs">
                            {(result.relevanceScore * 100).toFixed(0)}% match
                          </Badge>
                        </div>
                      </div>

                      <div className="mb-3 flex items-center gap-4 text-sm text-gray-600">
                        {result.recipe.cookMinutes && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {result.recipe.cookMinutes}m
                          </span>
                        )}
                        {result.recipe.difficulty && (
                          <span className="flex items-center gap-1">
                            <ChefHat className="h-4 w-4" />
                            {result.recipe.difficulty}
                          </span>
                        )}
                      </div>

                      {result.matchingFactors.length > 0 && (
                        <div className="mb-2">
                          <span className="text-xs text-gray-600">
                            Matching factors:{" "}
                          </span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {result.matchingFactors
                              .slice(0, 4)
                              .map(({ factor }, idx) => (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {factor}
                                </Badge>
                              ))}
                            {result.matchingFactors.length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{result.matchingFactors.length - 4} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {result.personalizedReasons.length > 0 && (
                        <div className="rounded bg-green-50 p-2 text-xs text-green-700">
                          {result.personalizedReasons[0]}
                          {result.personalizedReasons.length > 1 &&
                            ` +${result.personalizedReasons.length - 1} more reasons`}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Related Queries */}
      {relatedQueries.length > 0 && hasResults && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-sm">Related Searches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {relatedQueries.map((query, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery(query);
                    updateQuery(query);
                  }}
                  className="text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                >
                  {query}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
